import React, { useState } from 'react';
import { X, Download, FileText, IdCard } from 'lucide-react';
import { RegistrationStatus } from '../api/client';

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: ExportOptions) => void;
  eventTitle: string;
}

export type ExportType = 'csv' | 'badges' | 'badges-no-qr';

export interface ExportOptions {
  exportType: ExportType;
  statuses: RegistrationStatus[];
  includeFields: string[];
  badgeOptions?: {
    approvedOnly: boolean;
    totalBadges?: number;
  };
}

const ALL_FIELDS = [
  { id: 'id', label: 'Registration ID', default: true },
  { id: 'status', label: 'Status', default: true },
  { id: 'name', label: 'Name', default: true },
  { id: 'email', label: 'Email', default: true },
  { id: 'company', label: 'Company', default: true },
  { id: 'role', label: 'Role/Title', default: true },
  { id: 'pronouns', label: 'Pronouns', default: false },
  { id: 'phone', label: 'Phone', default: false },
  { id: 'relationship_type', label: 'Relationship Type', default: true },
  { id: 'dietary_requirements', label: 'Dietary Requirements', default: true },
  { id: 'non_dietary_requirements', label: 'Accessibility Requirements', default: true },
  { id: 'how_heard', label: 'How Heard', default: false },
  { id: 'newsletter_opt_in', label: 'Newsletter Opt-in', default: false },
  { id: 'ticket_code', label: 'Ticket Code', default: true },
  { id: 'created_at', label: 'Registration Date', default: true },
  { id: 'approved_at', label: 'Approval Date', default: false },
  { id: 'checked_in_at', label: 'Check-in Time', default: true },
  { id: 'email_verified', label: 'Email Verified', default: false },
  { id: 'notes', label: 'Notes', default: false },
];

const STATUS_OPTIONS = [
  { value: 'invited', label: 'Invited' },
  { value: 'opened', label: 'Opened' },
  { value: 'registered', label: 'Registered' },
  { value: 'pending', label: 'Pending' },
  { value: 'waitlist', label: 'Waitlist' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'checked_in', label: 'Checked In' },
  { value: 'declined', label: 'Declined' },
];

export const ExportDialog: React.FC<ExportDialogProps> = ({
  isOpen,
  onClose,
  onExport,
  eventTitle,
}) => {
  const [exportType, setExportType] = useState<ExportType>('csv');
  const [selectedStatuses, setSelectedStatuses] = useState<RegistrationStatus[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>(
    ALL_FIELDS.filter(f => f.default).map(f => f.id)
  );
  const [approvedOnly, setApprovedOnly] = useState(true);
  const [totalBadges, setTotalBadges] = useState<string>('');

  if (!isOpen) return null;

  const handleFieldToggle = (fieldId: string) => {
    setSelectedFields(prev =>
      prev.includes(fieldId)
        ? prev.filter(id => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  const handleStatusToggle = (status: RegistrationStatus) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleSelectAllStatuses = () => {
    setSelectedStatuses(STATUS_OPTIONS.map(opt => opt.value as RegistrationStatus));
  };

  const handleSelectNoStatuses = () => {
    setSelectedStatuses([]);
  };

  const handleSelectAll = () => {
    setSelectedFields(ALL_FIELDS.map(f => f.id));
  };

  const handleSelectNone = () => {
    setSelectedFields([]);
  };

  const handleExport = () => {
    const options: ExportOptions = {
      exportType,
      statuses: selectedStatuses,
      includeFields: selectedFields,
      badgeOptions: (exportType === 'badges' || exportType === 'badges-no-qr') ? {
        approvedOnly,
        totalBadges: totalBadges ? parseInt(totalBadges) : undefined
      } : undefined,
    };
    
    onExport(options);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative sm:top-20 mx-auto p-5 border w-full h-full sm:h-auto sm:max-w-2xl shadow-lg sm:rounded-lg bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Export Attendee Data</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Export data for <span className="font-medium">{eventTitle}</span>
          </p>
        </div>

        {/* Export Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Export Type
          </label>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => setExportType('csv')}
              className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                exportType === 'csv'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <FileText className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">CSV Data</div>
                <div className="text-xs text-gray-500">Spreadsheet format</div>
              </div>
            </button>
            <button
              onClick={() => setExportType('badges')}
              className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                exportType === 'badges'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <IdCard className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Name Badges</div>
                <div className="text-xs text-gray-500">PDF with QR</div>
              </div>
            </button>
            <button
              onClick={() => setExportType('badges-no-qr')}
              className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                exportType === 'badges-no-qr'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <IdCard className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Name Badges</div>
                <div className="text-xs text-gray-500">PDF no QR</div>
              </div>
            </button>
          </div>
        </div>

        {/* Badge Options (for badge export) */}
        {(exportType === 'badges' || exportType === 'badges-no-qr') ? (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Badge Options
            </label>
            <div className="border border-gray-200 rounded-md p-3 space-y-3">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={approvedOnly}
                  onChange={(e) => setApprovedOnly(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Only include approved attendees</span>
              </label>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Total badges to print (optional)
                </label>
                <input
                  type="number"
                  min="1"
                  value={totalBadges}
                  onChange={(e) => setTotalBadges(e.target.value)}
                  placeholder="Leave blank to print exact count"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  If more badges than attendees, blank badges will be added for manual name writing
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
        {/* Status Filter (for CSV export) */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Filter by Status
            </label>
            <div className="space-x-2">
              <button
                onClick={handleSelectAllStatuses}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Select All
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={handleSelectNoStatuses}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Select None
              </button>
            </div>
          </div>
          <div className="border border-gray-200 rounded-md p-3">
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes(option.value as RegistrationStatus)}
                    onChange={() => handleStatusToggle(option.value as RegistrationStatus)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Field Selection (for CSV export) */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Fields to Include
            </label>
            <div className="space-x-2">
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Select All
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={handleSelectNone}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Select None
              </button>
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
            <div className="grid grid-cols-2 gap-2">
              {ALL_FIELDS.map(field => (
                <label
                  key={field.id}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(field.id)}
                    onChange={() => handleFieldToggle(field.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{field.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        </>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exportType === 'csv' && selectedFields.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>{exportType === 'csv' ? 'Export CSV' : 'Export Badges PDF'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};